import { 
    useCallback,
    useContext
} from 'react';
import {
    doc,
    deleteDoc,
    writeBatch,
} from 'firebase/firestore';

import { firestore } from '../../services/startFirestore';
import { DocId } from '#types/System.types';
import { MessageContext } from '../../providers/MessageContext';

/**
 * Interface for data add operations
 */
export interface UseRemove {
    remove: (path: string, docId: DocId) => Promise<void>;
    batchRemove: (operations: Array<{ path: string; docId: DocId }>) => Promise<void>;
}

/**
 * Hook for managing Firestore data add operations
 * @param firestore - Firestore instance
 * @returns Object with add function
 */
export const useRemove = (): UseRemove => {
    const { handleFirestoreWriteError } = useContext(MessageContext);

    /**
     * Adds data to Firestore
     * @param path - Document path
     * @param data - Data to add
     * @returns Data or null if not found
     */
    const remove = useCallback(async (path: string, docId: DocId) => {
        const docRef = doc(firestore, path, docId);
        try {
            await deleteDoc(docRef);
        } catch (error) {
            throw handleFirestoreWriteError(error, '[useRemove] remove');
        }
    }, [handleFirestoreWriteError]);

    const batchRemove = useCallback(async (operations: Array<{ path: string; docId: DocId }>) => {
        const MAX_BATCH_SIZE = 450;
        try {
            for (let i = 0; i < operations.length; i += MAX_BATCH_SIZE) {
                const batch = writeBatch(firestore);
                const chunk = operations.slice(i, i + MAX_BATCH_SIZE);
                chunk.forEach(op => {
                    batch.delete(doc(firestore, op.path, op.docId));
                });
                await batch.commit();
            }
        } catch (error) {
            throw handleFirestoreWriteError(error, '[useRemove] batchRemove');
        }
    }, [handleFirestoreWriteError]);

    return { remove, batchRemove };
};